import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { clearToken, getUser, patientAPI } from "@/lib/api";
import { safeGetItem, safeSetItem, safeRemoveItem } from "@/lib/safeStorage";
import { isAutoAcceptStillActive } from "@/lib/autoaccept";
import { PatientSessionContext } from "./patient-session-context";
import type { PatientCase, PatientSessionContextValue } from "./patient-session-context";

// ─────────────────────────────────────────────────────────────────────────────
// PatientSessionProvider — sessão do Portal do Paciente acima do Router.
//
// POR QUE EXISTE:
//   Antes, cada página do portal (PacientePage, AgendarPage) restaurava a
//   sessão no próprio `useEffect(..., [])` e começava com `sessionLoading =
//   true`. Como /paciente/especialidade é uma rota IRMÃ de /paciente no router,
//   sair e voltar entre essas URLs desmontava/remontava a página, re-executando
//   o restore e mostrando "Restaurando sessão..." em tela cheia a cada volta.
//
//   Este provider vive ACIMA do <AppRoutes/> (dentro do BrowserRouter, para
//   poder redirecionar para /sala-espera quando necessário) e restaura a
//   sessão UMA única vez no carregamento inicial do app. A partir daí o
//   paciente fica em memória e a navegação interna é instantânea.
//
// REGRAS DE EXIBIÇÃO DO "Restaurando sessão...":
//   - Só aparece quando realmente NÃO temos sessão em memória/cache e existe
//     token de paciente a validar (app recém-aberto sem cache, ou troca de
//     conta para paciente). Com cache presente, renderiza imediatamente e
//     atualiza os dados em segundo plano (sem tela cheia de loading).
// ─────────────────────────────────────────────────────────────────────────────

// Alguns fluxos antigos de login (ex.: login por código OTP) salvaram o paciente
// no cache com os campos name/email/phone em vez de patient_name/patient_email/
// patient_phone. Telas como o PatientDashboard fazem `patient.patient_name.charAt(0)`,
// então um cache com o formato errado quebrava o portal com "Cannot read properties
// of undefined (reading 'charAt')". Este helper migra o formato antigo para o padrão
// usado pelo resto do app (e conserta o cache na escrita).
function normalizePatientShape(p: PatientCase | null | undefined): PatientCase | null {
  if (!p || typeof p !== "object" || p.id == null) return null;
  const n: Record<string, unknown> = { ...p };
  if (n.patient_name == null && n.name != null) n.patient_name = n.name;
  if (n.patient_email == null && n.email != null) n.patient_email = n.email;
  if (n.patient_phone == null && n.phone != null) n.patient_phone = n.phone;
  return n as unknown as PatientCase;
}

export function PatientSessionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const restoreInFlight = useRef(false);

  // Sessão lida de forma SÍNCRONA do cache para o primeiro render já mostrar o
  // portal (sem "Restaurando sessão..." quando já existe sessão válida).
  const [patient, setPatientState] = useState<PatientCase | null>(() => {
    try {
      const cached = JSON.parse(safeGetItem("logged_patient_case") || "null");
      return normalizePatientShape(cached);
    } catch {
      /* cache corrompido — ignora */
    }
    return null;
  });

  // Só liga o "Restaurando sessão..." quando não há sessão conhecida (sem id em
  // cache) mas existe token de paciente a validar. Com cache, já sabemos a
  // sessão → renderiza imediatamente e atualiza em background.
  const [sessionLoading, setSessionLoading] = useState<boolean>(() => {
    const raw = safeGetItem("logged_patient_case_id");
    if (raw) return false;
    const decoded = getUser<{ role?: string }>();
    return decoded?.role === "patient";
  });

  const patientRef = useRef(patient);
  useEffect(() => {
    patientRef.current = patient;
  }, [patient]);

  const setPatient = useCallback((p: PatientCase | null) => {
    const normalized = normalizePatientShape(p);
    setPatientState(normalized);
    if (normalized?.id) {
      try {
        safeSetItem("logged_patient_case", JSON.stringify(normalized));
        safeSetItem("logged_patient_case_id", JSON.stringify(normalized.id));
      } catch {
        /* quota excedida — mantém só em memória */
      }
    }
  }, []);

  const logout = useCallback(() => {
    clearToken();
    safeRemoveItem("logged_patient_case_id");
    safeRemoveItem("logged_patient_case");
    safeRemoveItem("active_emergency");
    safeRemoveItem("flow_type");
    safeRemoveItem("pending_slot");
    safeRemoveItem("pending_patient_case_id");
    safeRemoveItem("scheduling_info");
    safeRemoveItem("pre_registration_id");
    safeRemoveItem("form_data");
    safeRemoveItem("pre_registration_summary");
    safeRemoveItem("recent_whatsapp");
    setPatientState(null);
  }, []);

  // Token genuinamente expirado/inválido (ver api.ts) — mesmo tratamento já
  // aplicado em /medico e /admin (28/08/2026). Sessões de paciente abertas
  // antes desse deploy (tokens com prazo) ainda podem vencer uma última vez;
  // sem isso a pessoa ficava "logada" na tela vendo erro em toda ação, sem
  // saber que precisava entrar de novo.
  useEffect(() => {
    function handleExpired() {
      logout();
    }
    window.addEventListener("clinica:session-expired", handleExpired);
    return () => window.removeEventListener("clinica:session-expired", handleExpired);
  }, [logout]);

  // Login sumiu do aparelho com paciente na tela (api.ts → clinica:session-missing, 28/10/2026): encerra a sessão
  // para o portal mostrar o login com aviso. Sem paciente em memória (visitante), não faz nada.
  useEffect(() => {
    function handleMissing() {
      if (patientRef.current) logout();
    }
    window.addEventListener("clinica:session-missing", handleMissing);
    return () => window.removeEventListener("clinica:session-missing", handleMissing);
  }, [logout]);

  // ── Restaura a sessão (idempotente). Roda no carregamento inicial do app e
  //    também quando uma tela detecta token de paciente sem sessão em memória
  //    (ex.: troca de conta via AccountSwitcher). ──
  const restorePatient = useCallback(async () => {
    if (restoreInFlight.current) return;
    restoreInFlight.current = true;

    // Sem sessão conhecida em memória/cache → pode mostrar "Restaurando
    // sessão...". Com cache, atualiza em background (sem loading).
    if (!patientRef.current) setSessionLoading(true);

    try {
      const raw = safeGetItem("logged_patient_case_id");
      const decoded = getUser<{ role?: string }>();
      const isPatientToken = decoded?.role === "patient";
      if (!raw && !isPatientToken) return;
      // Login atual é de médico/admin: as rotas de paciente respondem 403 — não adianta chamar (eram dezenas de 403
      // por dia no aparelho do admin com um logged_patient_case_id antigo guardado, 26-28/10/2026).
      if (decoded?.role && !isPatientToken) return;

      let pc: PatientCase | null = null;
      try {
        const dashRes = await patientAPI.dashboard();
        pc = dashRes.data?.patient || dashRes.data;
      } catch {
        /* mantém o que houver em cache */
      }

      if (!pc?.id) {
        if (!raw) safeRemoveItem("logged_patient_case_id");
        return;
      }

      if (!raw) safeSetItem("logged_patient_case_id", JSON.stringify(pc.id));
      setPatient(pc as PatientCase);

      // AUTOACCEPT do Dr. Felipe Ferraz: consulta aceita automaticamente e
      // encaminhada pro WhatsApp. Mantém o paciente no portal — "Minhas
      // consultas" mostra o botão "Chamar no WhatsApp" (link direto), sem
      // re-encaminhar pro lobby (/sala-espera) nem pra videochamada enquanto
      // a consulta não for finalizada.
      try {
        const dashRes = await patientAPI.dashboard();
        const appts = dashRes.data?.appointments || [];
        const autoRaw = safeGetItem("autoaccept_whatsapp");
        if (autoRaw) {
          const auto = JSON.parse(autoRaw);
          if (auto && auto.queueId && String(auto.patientCaseId) === String(pc.id)) {
            if (isAutoAcceptStillActive(pc.id, appts)) {
              return;
            }
          }
        }
      } catch {
        /* ignora */
      }

      // Sala de espera de emergência ativa (menos de 10 min) → volta pra lá.
      // Só chega aqui quando NÃO há autoaccept ativo (consultas do Dr. Felipe
      // Ferraz ficam no portal com o botão "Chamar no WhatsApp").
      try {
        const emergencyRaw = safeGetItem("active_emergency");
        if (emergencyRaw) {
          const emergencyData = JSON.parse(emergencyRaw);
          const tenMinMs = 10 * 60 * 1000;
          if (
            emergencyData.emergencyId &&
            emergencyData.timestamp &&
            Date.now() - emergencyData.timestamp < tenMinMs
          ) {
            navigate(`/sala-espera/${emergencyData.emergencyId}`, { replace: true });
            return;
          }
        }
      } catch {
        /* ignora */
      }
    } finally {
      restoreInFlight.current = false;
      setSessionLoading(false);
    }
  }, [navigate, setPatient]);

  // Restaura UMA vez no carregamento inicial do app (o provider nunca é
  // desmontado durante a navegação interna, então isto não repete).
  useEffect(() => {
    restorePatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Atualização em segundo plano: não muda o sessionLoading nem a tela. ──
  const refreshPatient = useCallback(async (): Promise<PatientCase | null> => {
    try {
      const dashRes = await patientAPI.dashboard();
      const pc = dashRes.data?.patient || dashRes.data;
      if (pc?.id) {
        setPatient(pc as PatientCase);
        return pc as PatientCase;
      }
    } catch {
      /* mantém o cache em memória */
    }
    return null;
  }, [setPatient]);

  const value = useMemo<PatientSessionContextValue>(
    () => ({
      patient,
      sessionLoading,
      setPatient,
      refreshPatient,
      restorePatient,
      logout,
    }),
    [patient, sessionLoading, setPatient, refreshPatient, restorePatient, logout]
  );

  return (
    <PatientSessionContext.Provider value={value}>{children}</PatientSessionContext.Provider>
  );
}
